/**
 * lib/preview/tenant-menu-preview.ts — dane kartotek dla kreatora menu w scope
 * TENANTA: zunifikowany podgląd (parytet z hubem) + akordeon widoków pozycji
 * (feedback Eugenii 2026-07-02: „brakuje całej funkcji z admina").
 *
 * Pozycje menu klienta wskazują kartoteki dwojako:
 *  - `module` (moduleCode = slug kartoteki) — kartoteki z Kreatora (custom modules,
 *    w tym zainstalowane z szablonów) ALBO kartoteki systemowe (kopie menu modułów),
 *  - `custom_link` z trasą wewnętrzną (np. `/employees`) — kartoteki systemowe
 *    wstawione z palety (mapowanie trasa→slug po `MenuSourceKartoteka.path`).
 *
 * Źródła per slug:
 *  - systemowe: GET /api/kartoteki/{slug}/definition (READ-ONLY twin endpointu
 *    admina; wspólny builder w API),
 *  - z Kreatora: GET /api/modules/{slug} (pola z opcjami select + layouty form/list).
 *
 * Eksporty:
 *  - `useTenantMenuPreviewResolver` — sync `resolveNode` dla MenuUnifiedPreview
 *    (precedencja widoku pozycji: viewRef/listRef → domyślny layout kartoteki),
 *  - `useTenantKartotekaViews` — syntetyczne encje w kształcie szablonu
 *    (ModuleTemplateEntityDefinition) + resolver sluga dla MenuItemInlineEditor
 *    (akordeon: Ustawienia / Layout formularza / Widok list / Filtry stałe).
 */

import { useCallback, useMemo } from 'react';
import type {
  AdminMenuItem,
  ClientFormLayoutSection,
  ListConfig,
  MenuItem,
  MenuSourceKartoteka,
  ModuleTemplateEntityDefinition,
  ModuleTemplateFieldDefinition,
  ModuleTemplateViewDefinition,
} from '@saas/shared';
import { useQueries, useQueryClient } from '@tanstack/react-query';

import type { ModuleField } from '@/components/fields/FieldRegistry';
import type { MenuNodePreviewData } from '@/components/menu/MenuUnifiedPreview';
import { api } from '@/lib/api';
import { getClassForKartotekaSlug } from '@/lib/documents/class-kartoteka-slug';
import type { KartotekaDefinition } from '@/lib/kartoteki/kartoteka-definition';
import {
  mapKartotekaFieldsForPreview,
  mapKartotekaFieldType,
} from '@/lib/kartoteki/kartoteka-preview-fields';
import { parseMenuViewRef } from '@/lib/module-templates/template-layouts';

/** Kształt layoutConfig zapisany w bazie (sekcje klienta, fieldId = ID pola). */
interface RawLayoutConfig {
  sections?: Array<{
    id: string;
    title: string | null;
    columns?: number;
    elements?: Array<{
      id: string;
      type: string;
      fieldId?: string;
      width?: number;
      settings?: Record<string, unknown>;
    }>;
  }>;
  /**
   * Alternatywny kształt REKURENCYJNY (kartoteki systemowe — seed
   * `buildDefaultLayoutConfig` + layouty publikowane z buildera ITE):
   * drzewo elementów z zakładkami/sekcjami/polami.
   */
  elements?: RawRecursiveElement[];
}

interface RawRecursiveElement {
  id?: string;
  type?: string;
  fieldId?: string;
  width?: number;
  settings?: Record<string, unknown>;
  children?: RawRecursiveElement[];
  /** type='tabs' — zakładki z własnymi dziećmi. */
  tabs?: Array<{ id?: string; title?: string; children?: RawRecursiveElement[] }>;
}

/** Wspólny kształt layoutu kartoteki (systemowej lub z Kreatora). */
interface RawKartotekaLayout {
  id: string;
  layoutType: string;
  name?: string | null;
  isDefault: boolean;
  layoutConfig?: RawLayoutConfig | null;
  listConfig?: ListConfig | null;
}

/** Odpowiedź GET /api/modules/{slug} — wycinek potrzebny podglądowi/widokom. */
interface TenantModuleDetail {
  module: {
    id: string;
    slug: string;
    name: string;
    fields: ModuleField[];
    layouts: RawKartotekaLayout[];
  };
}

/** Znormalizowane dane jednej kartoteki (wspólne dla podglądu i akordeonu). */
interface TenantKartotekaData {
  entityName: string;
  /** Rodzaj kartoteki — decyduje o endpointach mutacji layoutów (etap b cz.2). */
  kind: 'system' | 'custom';
  /** UUID custom modułu (kind='custom') — endpointy /modules/[id]/layouts idą po ID. */
  moduleId?: string;
  fields: ModuleTemplateFieldDefinition[];
  /** ID pola → slug (adresowanie layoutów z bazy). */
  idToSlug: Map<string, string>;
  layouts: RawKartotekaLayout[];
  /** Domyślny widok listy (kartoteki systemowe — buildDefaultListConfig). */
  defaultListConfig?: ListConfig;
}

function clampColumns(v: unknown): 1 | 2 | 3 {
  return v === 1 || v === 3 ? v : 2;
}

function clampWidth(v: unknown): 1 | 2 | 3 {
  return v === 2 || v === 3 ? v : 1;
}

/**
 * Kształt rekurencyjny (kartoteki systemowe) → płaskie sekcje podglądu.
 * Zbiera węzły type='section' (tytuł/kolumny z settings, dzieci-pola spłaszczone),
 * schodzi w zakładki (`tabs`) i inne kontenery przez `children`. Pola luzem na
 * root trafiają do sekcji bez tytułu.
 */
function sectionsFromRecursive(
  elements: RawRecursiveElement[],
  idToSlug: ReadonlyMap<string, string>,
): ClientFormLayoutSection[] {
  const out: ClientFormLayoutSection[] = [];
  const loose: RawRecursiveElement[] = [];

  const fieldLeaves = (nodes: RawRecursiveElement[] | undefined): RawRecursiveElement[] => {
    const leaves: RawRecursiveElement[] = [];
    for (const n of nodes ?? []) {
      if (n.type === 'field') leaves.push(n);
      else if (n.children) leaves.push(...fieldLeaves(n.children));
    }
    return leaves;
  };

  const toElement = (el: RawRecursiveElement, i: number) => ({
    id: el.id ?? `el-${i}`,
    type: 'field',
    fieldId: el.fieldId ? (idToSlug.get(el.fieldId) ?? el.fieldId) : undefined,
    width: clampWidth(el.width),
    settings: el.settings ?? {},
  });

  const walk = (nodes: RawRecursiveElement[]) => {
    for (const node of nodes) {
      if (node.type === 'section') {
        out.push({
          id: node.id ?? `sec-${out.length}`,
          title: (node.settings?.title as string | undefined) ?? null,
          columns: clampColumns(node.settings?.columns),
          showHeader: true,
          showBorder: false,
          collapsible: false,
          elements: fieldLeaves(node.children).map(toElement),
        });
      } else if (node.type === 'tabs') {
        for (const tab of node.tabs ?? []) walk(tab.children ?? []);
      } else if (node.type === 'field') {
        loose.push(node);
      } else if (node.children) {
        walk(node.children);
      }
    }
  };
  walk(elements);

  if (loose.length > 0) {
    out.push({
      id: 'sec-loose',
      title: null,
      columns: 2,
      showHeader: false,
      showBorder: false,
      collapsible: false,
      elements: loose.map(toElement),
    });
  }
  return out;
}

/**
 * Sekcje layoutu z bazy → sekcje podglądu: fieldId (ID pola) tłumaczony na SLUG
 * (PreviewFormSections adresuje pola slugiem), szerokości/kolumny domykane do 1-3.
 * Obsługuje oba kształty: `{sections}` (kartoteki z Kreatora / custom_module_layouts)
 * i rekurencyjny `{elements}` (kartoteki systemowe).
 */
export function layoutSectionsForPreview(
  layoutConfig: RawLayoutConfig | null | undefined,
  idToSlug: ReadonlyMap<string, string>,
): ClientFormLayoutSection[] | undefined {
  const sections = layoutConfig?.sections;
  if (sections && sections.length > 0) {
    return sections.map((s) => ({
      id: s.id,
      title: s.title,
      columns: clampColumns(s.columns),
      showHeader: true,
      showBorder: false,
      collapsible: false,
      elements: (s.elements ?? []).map((el) => ({
        id: el.id,
        type: el.type,
        // ID pola → slug; element z nieznanym fieldId zostaje (render go pominie).
        fieldId: el.fieldId ? (idToSlug.get(el.fieldId) ?? el.fieldId) : undefined,
        width: clampWidth(el.width),
        settings: el.settings ?? {},
      })),
    }));
  }
  if (layoutConfig?.elements && layoutConfig.elements.length > 0) {
    const fromRecursive = sectionsFromRecursive(layoutConfig.elements, idToSlug);
    return fromRecursive.length > 0 ? fromRecursive : undefined;
  }
  return undefined;
}

/** Pola custom modułu (tenant) → pola buildera dla podglądu (z opcjami select). */
export function mapModuleFieldsForPreview(fields: ModuleField[]): ModuleTemplateFieldDefinition[] {
  return fields.map((f, i) => ({
    slug: f.slug,
    name: f.name,
    type: mapKartotekaFieldType(f.fieldType),
    isRequired: f.isRequired,
    sortOrder: i,
    options: (f.options ?? []).map((o) => ({ value: o.value, label: o.label })),
  }));
}

function dataFromKartotekaDefinition(def: KartotekaDefinition): TenantKartotekaData {
  return {
    entityName: def.module.name,
    kind: 'system',
    fields: mapKartotekaFieldsForPreview(def.fields),
    idToSlug: new Map(def.fields.map((f) => [f.id, f.slug])),
    layouts: def.layouts.map((l) => ({
      id: l.id,
      layoutType: l.layoutType,
      name: l.name,
      isDefault: l.isDefault,
      layoutConfig: l.layoutConfig as RawLayoutConfig | null,
      listConfig: (l.listConfig ?? null) as ListConfig | null,
    })),
    defaultListConfig: def.defaultListConfig as ListConfig | undefined,
  };
}

function dataFromModuleDetail(detail: TenantModuleDetail): TenantKartotekaData {
  const mod = detail.module;
  return {
    entityName: mod.name,
    kind: 'custom',
    moduleId: mod.id,
    fields: mapModuleFieldsForPreview(mod.fields),
    idToSlug: new Map(mod.fields.map((f) => [f.id, f.slug])),
    layouts: mod.layouts,
  };
}

function pickLayout(
  layouts: RawKartotekaLayout[],
  type: 'form' | 'list',
  layoutId?: string | null,
): RawKartotekaLayout | undefined {
  if (layoutId) return layouts.find((l) => l.layoutType === type && l.id === layoutId);
  return (
    layouts.find((l) => l.layoutType === type && l.isDefault) ??
    layouts.find((l) => l.layoutType === type)
  );
}

/**
 * Wspólne źródła danych kartotek (react-query dedupuje po kluczach między
 * hookami). Fetch per kartoteka z palety (`source.kartoteki`) — definicje
 * ładują się równolegle przy wejściu do edytora menu.
 */
function useTenantKartotekaSources(kartoteki: MenuSourceKartoteka[] | undefined) {
  const systemSlugs = useMemo(
    () =>
      (kartoteki ?? [])
        .filter((k) => k.kind === 'system')
        .map((k) => k.slug)
        .sort(),
    [kartoteki],
  );
  const customSlugs = useMemo(
    () =>
      (kartoteki ?? [])
        .filter((k) => k.kind === 'custom')
        .map((k) => k.slug)
        .sort(),
    [kartoteki],
  );
  // Trasa wewnętrzna → slug kartoteki systemowej (pozycje custom_link z palety).
  const pathToSlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const k of kartoteki ?? []) if (k.path) map.set(k.path, k.slug);
    return map;
  }, [kartoteki]);

  const systemData = useQueries({
    queries: systemSlugs.map((slug) => ({
      queryKey: ['tenant', 'kartoteki', slug, 'definition'],
      queryFn: async () =>
        (await api.get<KartotekaDefinition>(`/kartoteki/${slug}/definition`)).data,
      staleTime: 5 * 60_000,
      retry: false,
    })),
    combine: (results) => {
      const map = new Map<string, TenantKartotekaData>();
      systemSlugs.forEach((slug, i) => {
        const data = results[i]?.data;
        if (data) map.set(slug, dataFromKartotekaDefinition(data));
      });
      return map;
    },
  });

  const customData = useQueries({
    queries: customSlugs.map((slug) => ({
      queryKey: ['tenant', 'modules', slug, 'detail'],
      queryFn: async () => (await api.get<TenantModuleDetail>(`/modules/${slug}`)).data,
      staleTime: 5 * 60_000,
      retry: false,
    })),
    combine: (results) => {
      const map = new Map<string, TenantKartotekaData>();
      customSlugs.forEach((slug, i) => {
        const data = results[i]?.data;
        if (data) map.set(slug, dataFromModuleDetail(data));
      });
      return map;
    },
  });

  const getBySlug = useCallback(
    (slug: string) => systemData.get(slug) ?? customData.get(slug),
    [systemData, customData],
  );

  return { systemData, customData, pathToSlug, getBySlug };
}

/**
 * Buduje sync-owy `resolveNode` dla `MenuUnifiedPreview` w scope tenanta.
 * Precedencja widoku pozycji jak w hubie: nazwany layout (viewRef/listRef,
 * format 'kartotekaSlug:layoutId') → domyślny layout kartoteki.
 */
export function useTenantMenuPreviewResolver(
  kartoteki: MenuSourceKartoteka[] | undefined,
): (node: MenuItem) => MenuNodePreviewData | null {
  const { pathToSlug, getBySlug } = useTenantKartotekaSources(kartoteki);

  return useCallback(
    (node: MenuItem): MenuNodePreviewData | null => {
      let slug: string | null = null;
      if (node.type === 'module' && node.moduleCode) {
        slug = node.moduleCode;
      } else if (node.type === 'custom_link' && node.url) {
        slug = pathToSlug.get(node.url) ?? null;
      }
      if (!slug) return null;

      const data = getBySlug(slug);
      if (!data || data.fields.length === 0) return null;

      // Formularz: viewRef ('slug:layoutId') → wskazany layout; inaczej domyślny.
      const parsedForm = node.viewRef ? parseMenuViewRef(node.viewRef) : null;
      const formLayout = pickLayout(
        data.layouts,
        'form',
        parsedForm?.entitySlug === slug ? parsedForm.viewSlug : null,
      );
      // Lista: listRef → wskazany layout; inaczej domyślny / defaultListConfig.
      const parsedList = node.listRef ? parseMenuViewRef(node.listRef) : null;
      const listLayout = pickLayout(
        data.layouts,
        'list',
        parsedList?.entitySlug === slug ? parsedList.viewSlug : null,
      );

      return {
        entityName: data.entityName,
        entitySlug: slug,
        fields: data.fields,
        formConfig: undefined,
        clientFormSections: layoutSectionsForPreview(formLayout?.layoutConfig, data.idToSlug),
        listConfig: (listLayout?.listConfig ?? data.defaultListConfig) as ListConfig | undefined,
        formViewName: formLayout && !formLayout.isDefault ? (formLayout.name ?? null) : null,
        listViewName: listLayout && !listLayout.isDefault ? (listLayout.name ?? null) : null,
      };
    },
    [getBySlug, pathToSlug],
  );
}

/** Encje + resolver dla akordeonu widoków pozycji (MenuItemInlineEditor). */
export interface TenantKartotekaViews {
  entities: ModuleTemplateEntityDefinition[];
  resolveEntitySlug: (item: MenuItem | AdminMenuItem) => string | null;
}

/**
 * Syntetyczne encje w kształcie szablonu z danych kartotek tenanta — zasilają
 * akordeon widoków pozycji (`MenuItemInlineEditor`): lista layoutów formularza
 * i listy (slug widoku = ID layoutu w bazie; ref 'kartotekaSlug:layoutId'),
 * pola do filtrów stałych. `resolveEntitySlug` ogranicza akordeon widoków do
 * pozycji type='module' (schema `viewRef/listRef` dozwala tylko ten typ).
 */
export function useTenantKartotekaViews(
  kartoteki: MenuSourceKartoteka[] | undefined,
): TenantKartotekaViews {
  const { systemData, customData } = useTenantKartotekaSources(kartoteki);

  const entities = useMemo(() => {
    const out: ModuleTemplateEntityDefinition[] = [];
    let i = 0;
    const push = (slug: string, data: TenantKartotekaData) => {
      const views: ModuleTemplateViewDefinition[] = data.layouts
        .filter((l) => l.layoutType === 'form' || l.layoutType === 'list')
        .map((l, vi) => ({
          slug: l.id,
          name: l.name?.trim() || `Widok ${vi + 1}`,
          type: l.layoutType as 'form' | 'list',
          isDefault: l.isDefault,
          sortOrder: vi,
          listConfig: (l.listConfig ?? undefined) as ListConfig | undefined,
        }));
      out.push({
        slug,
        name: data.entityName,
        sortOrder: i++,
        navigationMode: 'sidebar_entry',
        // Celowo 'new_table': pola mamy wprost (filtry stałe czytają entity.fields).
        ddlStrategy: 'new_table',
        // Klasa dokumentu (gdy slug to kartoteka dokumentu) — odblokowuje picker typów dokumentów
        // w filtrach stałych, mimo ddlStrategy 'new_table'. Edytor rozdziela klasę od strategii
        // DDL, więc nie przełącza źródła pól na fetch admin-scope.
        documentClass: getClassForKartotekaSlug(slug) ?? undefined,
        fields: data.fields,
        views,
      });
    };
    for (const [slug, data] of systemData) push(slug, data);
    for (const [slug, data] of customData) push(slug, data);
    return out;
  }, [systemData, customData]);

  const resolveEntitySlug = useCallback(
    (item: MenuItem | AdminMenuItem) =>
      item.type === 'module' && item.moduleCode ? item.moduleCode : null,
    [],
  );

  return { entities, resolveEntitySlug };
}

// ---------------------------------------------------------------------------
// Mutacje layoutów kartotek z akordeonu (etap b cz.2, 2026-07-03)
// ---------------------------------------------------------------------------

/**
 * Akcje na layoutach kartoteki dostępne z akordeonu pozycji menu
 * (MenuItemInlineEditor: „+ Nowy" / „Duplikuj" / „Usuń" w sekcjach
 * Layout formularza i Widok list). Wszystkie async — edytor ignoruje
 * zwrotkę (fire-and-forget), odświeżenie idzie przez invalidację źródeł.
 */
export interface TenantKartotekaLayoutActions {
  /** Czy user może mutować layouty tej kartoteki (permission per kind). */
  canMutate: (entitySlug: string) => boolean;
  /** Nowy pusty layout (form) / kopia domyślnego (list). Zwraca ID nowego layoutu. */
  createLayout: (entitySlug: string, type: 'form' | 'list', name: string) => Promise<string>;
  /** Kopia layoutu (sourceLayoutId=null → kopia domyślnego). Zwraca ID kopii. */
  duplicateLayout: (
    entitySlug: string,
    type: 'form' | 'list',
    sourceLayoutId: string | null,
    name: string,
  ) => Promise<string>;
  /** Usuwa nazwany layout. */
  deleteLayout: (entitySlug: string, layoutId: string) => Promise<void>;
  /** Zmienia nazwę nazwanego layoutu (inline rename z akordeonu, 2026-07-03). */
  renameLayout: (entitySlug: string, layoutId: string, name: string) => Promise<void>;
  /**
   * Dane do buildera formularza (modal „Przeglądaj/edytuj" / „Podgląd" z akordeonu):
   * pola z REALNYMI ID (fieldId layoutów w bazie) + initialLayoutConfig w kształcie
   * drzewa buildera. layoutId=null → domyślny layout kartoteki (podgląd read-only).
   * null gdy kartoteka/layout nieznane (jeszcze się ładują).
   */
  getFormBuilderData: (
    entitySlug: string,
    layoutId: string | null,
  ) => TenantKartotekaFormBuilderData | null;
  /** Zapis layoutu formularza z buildera (drzewo → kształt bazy per kind + PUT). */
  saveFormLayout: (
    entitySlug: string,
    layoutId: string,
    builderTree: Record<string, unknown>,
  ) => Promise<void>;
  /**
   * Target buildera WIDOKU LISTY (modal z akordeonu, 2026-07-03): scope+code dla
   * SystemModuleListBuilderPage + wymuszony layout (sufiks tableId). layoutId=null
   * → domyślny zapisany layout listy (może nie istnieć — wtedy forcedLayoutId null,
   * builder pokaże stan domyślny). null gdy kartoteka nieznana.
   */
  getListBuilderTarget: (
    entitySlug: string,
    layoutId: string | null,
  ) => {
    scope: 'module' | 'system';
    code: string;
    forcedLayoutId: string | null;
    entityName: string;
    layoutName: string | null;
  } | null;
  /** Invalidacja źródeł kartoteki (po zamknięciu buildera listy — natywne PUT paneli). */
  invalidateKartoteka: (entitySlug: string) => Promise<void>;
}

/** Dane wejściowe modala buildera formularza (tenant, etap b cz.2). */
export interface TenantKartotekaFormBuilderData {
  entityName: string;
  /** Nazwa edytowanego layoutu (null = domyślny). */
  layoutName: string | null;
  kind: 'system' | 'custom';
  /** Pola w kształcie SystemModuleField (builder embedded) — REALNE ID pól. */
  fields: Array<{
    id: string;
    slug: string;
    name: string;
    fieldType: string;
    isCore: boolean;
    isSystemLocked: boolean;
    isRequired: boolean;
    sortOrder: number;
  }>;
  initialLayoutConfig: Record<string, unknown>;
}

// ── Translacja kształtów layoutu formularza: custom {sections} ↔ drzewo buildera ──
//
// Builder systemowy (SystemModuleBuilderPage embedded) operuje na rekurencyjnym
// drzewie {elements} z fieldId. Kartoteki systemowe trzymają layout w TYM kształcie
// (zero translacji). Kartoteki custom trzymają {sections} (RecordFormPage renderuje
// sekcje z kolumnami i szerokością pól) — tłumaczymy w obie strony; szerokość pola
// (element.width, brak odpowiednika w builderze) jedzie schowana w settings.__width
// i wraca przy zapisie (nowe pola bez __width → szerokość 1).

interface BuilderTreeElement {
  id: string;
  type: string;
  fieldId?: string;
  settings?: Record<string, unknown>;
  children?: BuilderTreeElement[];
  tabs?: Array<{ id?: string; title?: string; children?: BuilderTreeElement[] }>;
}

/**
 * custom {sections} → drzewo buildera {elements}. Builder systemowy organizuje
 * canvas po ZAKŁADKACH na roocie (bez taba sekcje się nie renderują — „Brak
 * zakładek"), więc sekcje jadą opakowane w jedną domyślną zakładkę „Dane"
 * (wzór buildDefaultLayout huba). Zapis spłaszcza taby z powrotem
 * (builderTreeToSections zbiera sekcje przez tabs/children).
 */
export function sectionsToBuilderTree(
  layoutConfig: RawLayoutConfig | null | undefined,
): Record<string, unknown> {
  const sections = layoutConfig?.sections ?? [];
  const sectionNodes = sections.map((s) => ({
    id: s.id,
    type: 'section',
    settings: { title: s.title, columns: clampColumns(s.columns) },
    children: (s.elements ?? []).map((el) => ({
      id: el.id,
      type: el.type,
      ...(el.fieldId ? { fieldId: el.fieldId } : {}),
      settings: { ...(el.settings ?? {}), __width: clampWidth(el.width) },
    })),
  }));
  return {
    elements: [
      {
        id: 'tenant-tabs-0',
        type: 'tabs',
        tabs: [
          {
            id: 'tenant-tab-0',
            slug: 'data',
            title: 'Dane',
            hidden: false,
            sortOrder: 0,
            children: sectionNodes,
          },
        ],
      },
    ],
  };
}

/**
 * Drzewo buildera {elements} → custom {sections}: zbiera węzły type='section'
 * (pola-dzieci spłaszczone, szerokość z settings.__width), schodzi w zakładki
 * i kontenery; pola luzem na root → sekcja bez tytułu (jak w podglądzie).
 */
export function builderTreeToSections(tree: Record<string, unknown>): RawLayoutConfig {
  const elements = (tree.elements ?? []) as BuilderTreeElement[];
  const out: NonNullable<RawLayoutConfig['sections']> = [];
  const loose: BuilderTreeElement[] = [];

  const fieldLeaves = (nodes: BuilderTreeElement[] | undefined): BuilderTreeElement[] => {
    const leaves: BuilderTreeElement[] = [];
    for (const n of nodes ?? []) {
      if (n.type === 'field') leaves.push(n);
      else if (n.tabs) for (const tab of n.tabs) leaves.push(...fieldLeaves(tab.children));
      else if (n.children) leaves.push(...fieldLeaves(n.children));
    }
    return leaves;
  };

  const toElement = (el: BuilderTreeElement) => {
    const { __width, ...settings } = el.settings ?? {};
    return {
      id: el.id,
      type: el.type,
      ...(el.fieldId ? { fieldId: el.fieldId } : {}),
      width: clampWidth(__width),
      settings,
    };
  };

  const walk = (nodes: BuilderTreeElement[]) => {
    for (const node of nodes) {
      if (node.type === 'section') {
        out.push({
          id: node.id,
          title: (node.settings?.title as string | null | undefined) ?? null,
          columns: clampColumns(node.settings?.columns),
          elements: fieldLeaves(node.children).map(toElement),
        });
      } else if (node.type === 'field') {
        loose.push(node);
      } else if (node.tabs) {
        for (const tab of node.tabs) walk(tab.children ?? []);
      } else if (node.children) {
        walk(node.children);
      }
    }
  };
  walk(elements);

  if (loose.length > 0) {
    out.push({ id: 'sec-loose', title: null, columns: 2, elements: loose.map(toElement) });
  }
  return { sections: out };
}

/** Pusty layout formularza w kształcie właściwym dla rodzaju kartoteki. */
function emptyFormLayoutConfig(kind: 'system' | 'custom'): Record<string, unknown> {
  if (kind === 'custom') {
    // custom_module_layouts: {sections} — RecordFormPage renderuje sekcje z kolumnami.
    return {
      sections: [{ id: crypto.randomUUID(), title: null, columns: 2, elements: [] }],
    };
  }
  // system_module_layouts: rekurencyjne {elements} (walidator layout-config-validator).
  return { elements: [] };
}

/**
 * Hook z akcjami mutacji layoutów kartotek (tenant). Endpointy per rodzaj:
 *  - custom (z Kreatora / własne): /modules/{moduleId}/layouts (ID modułu!),
 *  - systemowe: /system-modules/{slug}/layouts.
 * Po mutacji invalidacja źródła kartoteki → syntetyczne encje akordeonu i podgląd
 * odświeżają się same. Uprawnienia: caller podaje flagi manage (z /me) — canMutate
 * rozstrzyga per kind; backend i tak waliduje (403).
 */
export function useTenantKartotekaLayoutActions(
  kartoteki: MenuSourceKartoteka[] | undefined,
  permissions: { canManageCustom: boolean; canManageSystem: boolean },
): TenantKartotekaLayoutActions {
  const { getBySlug } = useTenantKartotekaSources(kartoteki);
  const queryClient = useQueryClient();

  const invalidate = useCallback(
    async (slug: string, kind: 'system' | 'custom') => {
      await queryClient.invalidateQueries({
        queryKey:
          kind === 'system'
            ? ['tenant', 'kartoteki', slug, 'definition']
            : ['tenant', 'modules', slug, 'detail'],
      });
    },
    [queryClient],
  );

  const layoutsBase = useCallback(
    (slug: string): { base: string; kind: 'system' | 'custom' } | null => {
      const data = getBySlug(slug);
      if (!data) return null;
      if (data.kind === 'custom') {
        if (!data.moduleId) return null;
        return { base: `/modules/${data.moduleId}/layouts`, kind: 'custom' };
      }
      return { base: `/system-modules/${slug}/layouts`, kind: 'system' };
    },
    [getBySlug],
  );

  const canMutate = useCallback(
    (slug: string) => {
      const data = getBySlug(slug);
      if (!data) return false;
      return data.kind === 'custom' ? permissions.canManageCustom : permissions.canManageSystem;
    },
    [getBySlug, permissions.canManageCustom, permissions.canManageSystem],
  );

  const createLayout = useCallback(
    async (slug: string, type: 'form' | 'list', name: string) => {
      const target = layoutsBase(slug);
      const data = getBySlug(slug);
      if (!target || !data) throw new Error(`Nieznana kartoteka: ${slug}`);
      // Lista bez listConfig byłaby bezużyteczna — nowy widok listy startuje jako
      // kopia domyślnego (hub analogicznie seeduje defaultem kartoteki).
      const defaultList =
        data.layouts.find((l) => l.layoutType === 'list' && l.isDefault)?.listConfig ??
        data.defaultListConfig ??
        null;
      const body: Record<string, unknown> = {
        layoutType: type,
        name,
        isDefault: false,
        layoutConfig: type === 'form' ? emptyFormLayoutConfig(data.kind) : {},
        ...(type === 'list' && defaultList ? { listConfig: defaultList } : {}),
      };
      const res = await api.post<{ layout: { id: string } }>(target.base, body);
      await invalidate(slug, target.kind);
      return res.data.layout.id;
    },
    [layoutsBase, getBySlug, invalidate],
  );

  const duplicateLayout = useCallback(
    async (slug: string, type: 'form' | 'list', sourceLayoutId: string | null, name: string) => {
      const target = layoutsBase(slug);
      const data = getBySlug(slug);
      if (!target || !data) throw new Error(`Nieznana kartoteka: ${slug}`);
      // Źródło kopii: wskazany layout albo (null = wiersz „Domyślny") domyślny layout
      // kartoteki; dla listy fallback na defaultListConfig (systemowe bez zapisanego).
      const source = sourceLayoutId
        ? data.layouts.find((l) => l.id === sourceLayoutId)
        : (data.layouts.find((l) => l.layoutType === type && l.isDefault) ??
          data.layouts.find((l) => l.layoutType === type));
      const layoutConfig =
        type === 'form'
          ? ((source?.layoutConfig ?? emptyFormLayoutConfig(data.kind)) as Record<string, unknown>)
          : {};
      const listConfig =
        type === 'list' ? (source?.listConfig ?? data.defaultListConfig ?? null) : null;
      const body: Record<string, unknown> = {
        layoutType: type,
        name,
        isDefault: false,
        layoutConfig,
        ...(listConfig ? { listConfig } : {}),
      };
      const res = await api.post<{ layout: { id: string } }>(target.base, body);
      await invalidate(slug, target.kind);
      return res.data.layout.id;
    },
    [layoutsBase, getBySlug, invalidate],
  );

  const deleteLayout = useCallback(
    async (slug: string, layoutId: string) => {
      const target = layoutsBase(slug);
      if (!target) throw new Error(`Nieznana kartoteka: ${slug}`);
      await api.delete(`${target.base}/${layoutId}`);
      await invalidate(slug, target.kind);
    },
    [layoutsBase, invalidate],
  );

  const renameLayout = useCallback(
    async (slug: string, layoutId: string, name: string) => {
      const target = layoutsBase(slug);
      if (!target) throw new Error(`Nieznana kartoteka: ${slug}`);
      await api.put(`${target.base}/${layoutId}`, { name });
      await invalidate(slug, target.kind);
    },
    [layoutsBase, invalidate],
  );

  const getFormBuilderData = useCallback(
    (slug: string, layoutId: string | null): TenantKartotekaFormBuilderData | null => {
      const data = getBySlug(slug);
      if (!data) return null;
      const layout = layoutId
        ? data.layouts.find((l) => l.id === layoutId)
        : (data.layouts.find((l) => l.layoutType === 'form' && l.isDefault) ??
          data.layouts.find((l) => l.layoutType === 'form'));
      // Pola z REALNYMI ID: odwracamy idToSlug (fields są slug-keyed po mapowaniu).
      const slugToId = new Map<string, string>();
      for (const [id, s] of data.idToSlug) slugToId.set(s, id);
      const fields = data.fields.map((f, i) => ({
        id: slugToId.get(f.slug) ?? f.slug,
        slug: f.slug,
        name: f.name,
        fieldType: f.type,
        isCore: false,
        isSystemLocked: false,
        isRequired: f.isRequired ?? false,
        sortOrder: f.sortOrder ?? i,
      }));
      const raw = layout?.layoutConfig ?? null;
      // custom: {sections} → drzewo buildera; layout zapisany już w kształcie
      // {elements} (np. zainstalowany z szablonu) przechodzi bez translacji.
      const initialLayoutConfig =
        data.kind === 'custom' && !(raw?.elements && raw.elements.length > 0)
          ? sectionsToBuilderTree(raw)
          : ((raw ?? { elements: [] }) as unknown as Record<string, unknown>);
      return {
        entityName: data.entityName,
        layoutName: layout && !layout.isDefault ? (layout.name ?? null) : null,
        kind: data.kind,
        fields,
        initialLayoutConfig,
      };
    },
    [getBySlug],
  );

  const saveFormLayout = useCallback(
    async (slug: string, layoutId: string, builderTree: Record<string, unknown>) => {
      const target = layoutsBase(slug);
      const data = getBySlug(slug);
      if (!target || !data) throw new Error(`Nieznana kartoteka: ${slug}`);
      // custom: z powrotem do {sections} (RecordFormPage); systemowe: drzewo as-is
      // (walidator layout-config-validator przyjmuje rekurencyjne {elements}).
      const layoutConfig =
        data.kind === 'custom'
          ? (builderTreeToSections(builderTree) as unknown as Record<string, unknown>)
          : builderTree;
      await api.put(`${target.base}/${layoutId}`, { layoutConfig });
      await invalidate(slug, target.kind);
    },
    [layoutsBase, getBySlug, invalidate],
  );

  const getListBuilderTarget = useCallback(
    (slug: string, layoutId: string | null) => {
      const data = getBySlug(slug);
      if (!data) return null;
      const layout = layoutId
        ? data.layouts.find((l) => l.id === layoutId)
        : data.layouts.find((l) => l.layoutType === 'list' && l.isDefault);
      return {
        scope: data.kind === 'custom' ? ('module' as const) : ('system' as const),
        // scope 'module': builder sam rozwiązuje slug→id (GET /modules/{slug});
        // scope 'system': slug wprost.
        code: slug,
        forcedLayoutId: layout?.id ?? null,
        entityName: data.entityName,
        layoutName: layout && !layout.isDefault ? (layout.name ?? null) : null,
      };
    },
    [getBySlug],
  );

  const invalidateKartoteka = useCallback(
    async (slug: string) => {
      const data = getBySlug(slug);
      if (!data) return;
      await invalidate(slug, data.kind);
    },
    [getBySlug, invalidate],
  );

  return {
    canMutate,
    createLayout,
    duplicateLayout,
    deleteLayout,
    renameLayout,
    getFormBuilderData,
    saveFormLayout,
    getListBuilderTarget,
    invalidateKartoteka,
  };
}
